/**
 * Shared chart chrome: one axis/tick/grid style, axis titles, plot heights and
 * margins for every Recharts-backed chart.
 */
import type { CSSProperties } from 'react';
import { axisTickFormatter } from './chartAxes';
import type { FmChartSize } from './chartTypes';

/**
 * Numeric form of `--fm-fs-fine` (12px). Recharts needs tick sizes as numbers;
 * 12px is the smallest legible axis step at a 390px viewport.
 */
export const CHART_AXIS_FONT_SIZE = 12;

/** Room for locale-formatted seven-figure ticks plus the axis title. */
export const CHART_Y_AXIS_WIDTH = 72;

/** Room under the plot for the x-axis title. */
export const CHART_AXIS_TITLE_GUTTER = CHART_AXIS_FONT_SIZE + 8;

/** Plot heights mirror `--fm-chart-plot-h-*`; Recharts needs numbers. */
export const CHART_PLOT_HEIGHT: Readonly<Record<FmChartSize, number>> = {
  sm: 200,
  md: 260,
  lg: 320,
};

export const CHART_MARGIN = {
  top: 16,
  right: 24,
  bottom: CHART_AXIS_TITLE_GUTTER,
  left: 8,
} as const;

export const CHART_GRID_PROPS = {
  strokeDasharray: '0',
  stroke: 'var(--fm-chart-grid)',
  horizontal: true,
  vertical: false,
};

const AXIS_LINE = { stroke: 'var(--fm-chart-axis)' };

export const CHART_XAXIS_PROPS = {
  tick: { fill: 'var(--fm-chart-label)', fontSize: CHART_AXIS_FONT_SIZE },
  axisLine: AXIS_LINE,
  tickLine: AXIS_LINE,
};

export const CHART_YAXIS_PROPS = {
  width: CHART_Y_AXIS_WIDTH,
  // Steps of 1, 2 or 5 times a power of ten (0, 500, 1 000 …), never 450.
  niceTicks: 'snap125' as const,
  tickFormatter: axisTickFormatter,
  tick: { fill: 'var(--fm-chart-label)', fontSize: CHART_AXIS_FONT_SIZE },
  axisLine: AXIS_LINE,
  tickLine: AXIS_LINE,
};

/** Axis-title text style shared by every Recharts axis `label`. */
export const CHART_AXIS_LABEL_STYLE = {
  fill: 'var(--fm-chart-label-muted)',
  fontSize: CHART_AXIS_FONT_SIZE,
};

/** Axis titles are required on every cartesian chart. */
export function xAxisTitle(value: string) {
  return {
    value: formatChartUnit(value),
    position: 'insideBottom' as const,
    offset: -CHART_AXIS_TITLE_GUTTER + 4,
    style: CHART_AXIS_LABEL_STYLE,
  };
}

/**
 * A bare unit of up to three characters (€, %, m³) reads as a glyph, not a
 * word, once turned on its side: a rotated € looked like a stray chevron. It
 * sits upright above the tick column instead, where the eye meets the axis.
 */
const UPRIGHT_UNIT_MAX_LENGTH = 3;

function isUprightUnit(title: string): boolean {
  return title.length > 0 && title.length <= UPRIGHT_UNIT_MAX_LENGTH;
}

/** Height of the upright unit line above the plot, clear of the top tick. */
const UPRIGHT_UNIT_ROOM = CHART_AXIS_FONT_SIZE + 6;

/** The plot margin for a y-axis titled `yLabel`: an upright unit needs a line above the top tick. */
export function chartMargin(yLabel: string): Record<keyof typeof CHART_MARGIN, number> {
  return isUprightUnit(formatChartUnit(yLabel))
    ? { ...CHART_MARGIN, top: CHART_MARGIN.top + UPRIGHT_UNIT_ROOM }
    : CHART_MARGIN;
}

export function yAxisTitle(value: string) {
  const title = formatChartUnit(value);
  if (isUprightUnit(title)) {
    return {
      value: title,
      position: 'top' as const,
      offset: UPRIGHT_UNIT_ROOM - CHART_AXIS_FONT_SIZE / 2,
      // Right-aligned with the tick numbers it qualifies: Recharts' default
      // tick line (6) and tick gap (2) end them 8px short of the axis.
      dx: CHART_Y_AXIS_WIDTH / 2 - 8,
      style: { ...CHART_AXIS_LABEL_STYLE, textAnchor: 'end' as const },
    };
  }
  return {
    value: title,
    angle: -90,
    position: 'insideLeft' as const,
    style: { ...CHART_AXIS_LABEL_STYLE, textAnchor: 'middle' as const },
  };
}

/** Emphasis for marks outside the active key or category. */
export const CHART_DIMMED_OPACITY = 0.35;

/**
 * Units print the way owners read them: m³, m², €/ha. Served unit strings
 * arrive in ASCII ("m3/ha", "EUR/ha"), so the chrome normalizes once.
 */
export function formatChartUnit(unit: string): string {
  return unit
    .replace(/\bm3\b/g, 'm³')
    .replace(/\bm2\b/g, 'm²')
    .replace(/\bEUR\b|\beur\b/g, '€');
}

/**
 * Recharts portals the tooltip outside the DS surface, so its frame is applied
 * inline from the `--fm-chart-tooltip-*` tokens.
 */
export const FM_CHART_TOOLTIP_FRAME: CSSProperties = {
  background: 'var(--fm-chart-tooltip-bg)',
  border: 'var(--fm-chart-tooltip-border)',
  borderRadius: 'var(--fm-chart-tooltip-radius)',
  padding: 'var(--fm-chart-tooltip-pad)',
  boxShadow: 'var(--fm-chart-tooltip-shadow)',
  fontSize: 'var(--fm-chart-tooltip-fs)',
  lineHeight: 'var(--fm-chart-tooltip-lh)',
  minWidth: 'var(--fm-chart-tooltip-min-w)',
  color: 'var(--fm-d-fg)',
};
